import { Children, isValidElement } from 'react';
import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '#/modules/commonComponents/tabs';

function TabbedDetailBar({
  title,
  actions,
  hint,
  className,
  titleClassName,
  actionsClassName,
}) {
  return (
    <div
      className={cn(
        'flex min-h-14 shrink-0 items-center justify-between gap-3 px-4',
        'border-y border-primary-200/70',
        'bg-gradient-to-b from-primary-50 to-primary-100',
        className,
      )}
    >
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1',
          titleClassName,
        )}
      >
        <h3 className="shrink-0 text-base font-semibold text-gray-800">{title}</h3>
        {hint ? (
          <p className="text-sm text-amber-800">{hint}</p>
        ) : null}
      </div>
      {actions ? (
        <div
          className={cn(
            'flex shrink-0 flex-wrap items-center justify-end gap-2 self-center',
            actionsClassName,
          )}
        >
          {actions}
        </div>
      ) : null}
    </div>
  );
}

TabbedDetailBar.propTypes = {
  title: PropTypes.string.isRequired,
  actions: PropTypes.node,
  hint: PropTypes.node,
  className: PropTypes.string,
  titleClassName: PropTypes.string,
  actionsClassName: PropTypes.string,
};

function TabbedDetailCardRoot({
  defaultValue,
  value,
  onValueChange,
  children,
  className,
}) {
  return (
    <Tabs
      defaultValue={defaultValue}
      value={value}
      onValueChange={onValueChange}
      className={cn('w-full', className)}
    >
      <div className="receipt-card-elevate">
        <div className="receipt-card-surface flex flex-col">
          {children}
        </div>
      </div>
    </Tabs>
  );
}

TabbedDetailCardRoot.propTypes = {
  defaultValue: PropTypes.string,
  value: PropTypes.string,
  onValueChange: PropTypes.func,
  children: PropTypes.node,
  className: PropTypes.string,
};

function TabbedDetailCardList({ children, className }) {
  return (
    <TabsList
      className={cn(
        'h-auto w-full flex-wrap justify-start rounded-none',
        'border-b border-slate-200 bg-slate-200 p-1',
        className,
      )}
    >
      {children}
    </TabsList>
  );
}

TabbedDetailCardList.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

function TabbedDetailCardTrigger({ value, children, className, disabled }) {
  return (
    <TabsTrigger value={value} className={className} disabled={disabled}>
      {children}
    </TabsTrigger>
  );
}

TabbedDetailCardTrigger.propTypes = {
  value: PropTypes.string.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  disabled: PropTypes.bool,
};

function TabbedDetailCardBar({
  title,
  actions,
  hint,
  className,
  titleClassName,
  actionsClassName,
}) {
  return (
    <TabbedDetailBar
      title={title}
      actions={actions}
      hint={hint}
      className={className}
      titleClassName={titleClassName}
      actionsClassName={actionsClassName}
    />
  );
}

TabbedDetailCardBar.displayName = 'TabbedDetailCard.Bar';

TabbedDetailCardBar.propTypes = {
  title: PropTypes.string.isRequired,
  actions: PropTypes.node,
  hint: PropTypes.node,
  className: PropTypes.string,
  titleClassName: PropTypes.string,
  actionsClassName: PropTypes.string,
};

function TabbedDetailCardContent({ children, className, contentClassName }) {
  return (
    <div
      className={cn(
        'tabbed-detail-card__content',
        'document-detail-panel stock-receipt-detail-section__scroll min-h-100 bg-transparent pb-0!',
        contentClassName,
        className,
      )}
    >
      {children}
    </div>
  );
}

TabbedDetailCardContent.displayName = 'TabbedDetailCard.Content';

TabbedDetailCardContent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
};

function isTabbedDetailBarChild(child) {
  return isValidElement(child) && child.type?.displayName === 'TabbedDetailCard.Bar';
}

function isTabbedDetailContentChild(child) {
  return isValidElement(child) && child.type?.displayName === 'TabbedDetailCard.Content';
}

function TabbedDetailCardPane({
  value,
  title,
  actions,
  hint,
  children,
  paneClassName,
  barClassName,
  titleClassName,
  actionsClassName,
  contentClassName,
  className,
}) {
  const childArray = Children.toArray(children);
  const hasBarChild = childArray.some(isTabbedDetailBarChild);
  const hasContentChild = childArray.some(isTabbedDetailContentChild);

  return (
    <TabsContent
      value={value}
      className={cn('mt-0 flex flex-col', paneClassName, className)}
    >
      {!hasBarChild && title ? (
        <TabbedDetailCardBar
          title={title}
          actions={actions}
          hint={hint}
          className={barClassName}
          titleClassName={titleClassName}
          actionsClassName={actionsClassName}
        />
      ) : null}
      {hasBarChild || hasContentChild ? (
        children
      ) : (
        <TabbedDetailCardContent contentClassName={contentClassName}>
          {children}
        </TabbedDetailCardContent>
      )}
    </TabsContent>
  );
}

TabbedDetailCardPane.propTypes = {
  value: PropTypes.string.isRequired,
  title: PropTypes.string,
  actions: PropTypes.node,
  hint: PropTypes.node,
  children: PropTypes.node,
  paneClassName: PropTypes.string,
  barClassName: PropTypes.string,
  titleClassName: PropTypes.string,
  actionsClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  className: PropTypes.string,
};

const TabbedDetailCard = Object.assign(TabbedDetailCardRoot, {
  List: TabbedDetailCardList,
  Trigger: TabbedDetailCardTrigger,
  Pane: TabbedDetailCardPane,
  Bar: TabbedDetailCardBar,
  Content: TabbedDetailCardContent,
});

export { TabbedDetailBar };
export default TabbedDetailCard;
